import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { Grid, Grow } from '@mui/material';
import styled from '@emotion/styled';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '@/redux/hooks';
import {
  selectChildrenByTitleId,
  selectChildrenLoadingStatusByTitleId,
  selectTitleLoadingStatusById,
  selectTitleV2ById
} from '@/redux/selectors/titleV2Selectors';
import { LoadingStatuses } from '@/constants/constants';
import BrowserItemSkeletonV2 from '@/components/TitleDetail/v2/TitleBrowserV2/BrowserItemSkeletonV2';
import TitleBrowserItemV2 from '@/components/TitleDetail/v2/TitleBrowserV2/TitleBrowserItemV2';
import { useTitleURLParser } from '@/components/TitleDetail/v2/hooks/useTitleURLParser';
import { buildTitleURL } from '@/_utils/titleUtils';
import { useQuery } from '@/components/_hooks/useQuery';

interface Props {
  titleId: number;
  /**
   * nestLevel starts from 1.
   * 1 means TitleBrowserListV2 displays children for the root title. For example, if root title is Series, then rendered children are seasons.
   * 2 means TitleBrowserListV2 displays children of the child of the root title. For example, if root title is Series, then rendered children on the 2nd nestLevel are episodes.
   */
  nestLevel: number;
  /**
   * There are two ways to know how many children the title has:
   *  1) by requesting server for the children of the title
   *  2) by reading the parent title's `relationships.children` array
   *  In situations when user navigates title's browser from top level title down to the children, in most cases the
   *  parent title will be loaded before children request is event started, so it makes sense to use that information
   *  to know how many items the list will have even before we started loading the children list.
   *
   *  This prop can be `undefined` in case if parent title entity is not loaded yet, in case when we open the page
   *  containing a path to the child (not just a root title), for example `/titles/12/13/156`.
   */
  itemsCountBasedOnParent: number | undefined;
}

const useAutoScrollToLastTitlesList = (selectedTitleId: number | undefined, scrollToElRef: MutableRefObject<HTMLDivElement | null>) => {
  const selectedTitleLoaded = useAppSelector((state) => selectTitleLoadingStatusById(state, selectedTitleId)) === LoadingStatuses.fulfilled;
  const selectedTitle = useAppSelector((state) => selectTitleV2ById(state, selectedTitleId));
  const selectedTitleHasChildren = Boolean(selectedTitle?.relationships.children.find((c) => c.entity_type !== 'version'));
  /**
   * This list is last if it doesn't have a selected title (if not title is selected, then there is no reason to show the children of that title),
   * or if we know that selected title doesn't have children.
   */
  const isLastTitleList = !selectedTitleId || (selectedTitleLoaded && !selectedTitleHasChildren);

  // Automatically scroll to the last displayed titles list
  useEffect(() => {
    if (isLastTitleList && scrollToElRef.current) {
      scrollToElRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isLastTitleList, scrollToElRef.current]);
};

const TitleBrowserListV2: React.FC<Props> = ({ titleId, nestLevel, itemsCountBasedOnParent }) => {
  const history = useHistory();
  const query = useQuery();
  const tab = query.get('tab');
  const areChildrenLoading = useAppSelector((state) => selectChildrenLoadingStatusByTitleId(state, titleId)) === LoadingStatuses.pending;
  const children = useAppSelector((state) => selectChildrenByTitleId(state, titleId));
  const { path } = useTitleURLParser();
  const selectedTitleId: number | undefined = path[nestLevel]; // selected title id on the current nesting level
  const isGrandchildSelected = typeof path[nestLevel + 1] !== 'undefined';
  const listElRef = useRef<HTMLDivElement | null>(null);
  const itemsElsRefs = useRef<Record<number, HTMLDivElement>>({});
  /**
   * This ref is used when we refresh titles (re-fetch them from the server) to avoid `TitleBrowserListV2` from changing its height,
   * to prevent the content on the page jump up and down.
   */
  const lastLoadedChildrenCount = useRef<number | null>(null);

  useAutoScrollToLastTitlesList(selectedTitleId, listElRef);

  const assignElementRef = (index: number, element: HTMLDivElement) => {
    itemsElsRefs.current[index] = element;
  };

  // Automatically scroll to selected title item
  useEffect(() => {
    if (!areChildrenLoading && listElRef.current && selectedTitleId !== undefined) {
      const listOffset = listElRef.current?.getBoundingClientRect().top;
      const itemOffset = itemsElsRefs.current[selectedTitleId]?.getBoundingClientRect().top;

      const itemRelativeOffset = itemOffset - listOffset - 240;
      // For some reason, autoscrolling to the selected item sometimes doesn't work without `setTimeout`
      setTimeout(() => {
        // We have to use `scrollTo` instead of `scrollIntoView` make sure that we scrolling only inside the current `TitleBrowserListV2`, not the `TitleBrowserV2`.
        //  `TitleBrowserV2` has its own horizontal scrollbar which is handled separately and we don't want to affect its scroll position by using `scrollIntoView`.
        listElRef.current?.scrollTo({
          top: itemRelativeOffset,
          behavior: 'smooth'
        });
      }, 0);
    }
  }, [areChildrenLoading]);

  useEffect(() => {
    if (!areChildrenLoading && children?.length) {
      lastLoadedChildrenCount.current = children.length;
    }
  }, [areChildrenLoading, children?.length]);

  const handleTitleClick = useCallback(
    (clickedTitleId: number) => {
      const newPath = path.slice(0, nestLevel);
      newPath[nestLevel] = clickedTitleId;
      history.push(buildTitleURL({ path: newPath, tab: tab ?? undefined }));
    },
    [nestLevel, path, tab]
  );

  if (!areChildrenLoading && !children?.length) {
    return null;
  }

  let skeletonItemsCount: number | undefined;

  if (itemsCountBasedOnParent) {
    skeletonItemsCount = itemsCountBasedOnParent;
  } else if (lastLoadedChildrenCount.current) {
    skeletonItemsCount = lastLoadedChildrenCount.current;
  } else {
    skeletonItemsCount = 1;
  }

  skeletonItemsCount = Math.min(skeletonItemsCount, 6); // limit skeleton items count to 6, because there is no reason to render more items than fit in the viewport

  return (
    <Container
      item
      xs={12}
      sm={6}
      style={{ minHeight: areChildrenLoading && lastLoadedChildrenCount.current ? `${lastLoadedChildrenCount.current}px` : undefined }}
    >
      <List ref={listElRef}>
        {areChildrenLoading ? (
          Array.from({
            length: skeletonItemsCount
          }).map((_, index) => <BrowserItemSkeletonV2 key={index} isLast={index === skeletonItemsCount - 1} />)
        ) : (
          <>
            {(children ?? []).map((child, i) => (
              <Grow
                key={child.id}
                in
                timeout={
                  Math.min(i, 6) * 250 // if the list is long, we don't want the animation to take too long. That's why timeout multiplier is capped.
                }
              >
                <TitleBrowserItemV2
                  ref={(el: HTMLDivElement | null) => {
                    if (el) assignElementRef(child.id, el);
                  }}
                  title={child}
                  active={!isGrandchildSelected && selectedTitleId === child.id}
                  activeParent={isGrandchildSelected && selectedTitleId === child.id}
                  isLastChild={children && i === children.length - 1}
                  onItemClick={handleTitleClick}
                />
              </Grow>
            ))}
          </>
        )}
      </List>
    </Container>
  );
};

export default TitleBrowserListV2;

const Container = styled(Grid)`
  flex-shrink: 0;
  position: relative;
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const List = styled.div`
  // The height of the title item is 46px (when text inside it fits in one row) and the margin between items is 10px.
  //  Max height allows to fit 6 items, so: 46 * 6 + 10 * 5 = 326.
  max-height: 326px;
  overflow: auto;
  padding-right: ${({ theme }) => theme.spacing(1)};
`;
